import { useQuery } from "@tanstack/react-query";
import { GET_MY_PROGRESS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { useProductFeatures } from "./useProductFeatures";
import type {
  ClimberProgress,
  ClimberFacets,
  LadderProgress,
  ProfileChoices,
} from "../types/mmp-graphql";

interface GetMyProgressResponse {
  getMyProgress: ClimberProgress | null;
}

/**
 * Loads the signed-in climber's progress record for the active product
 * (DynamoDB id: prg-<productCode>). Progress records are created lazily on
 * first write, so a null result simply means "no progress yet" and is
 * surfaced as empty collections.
 */
export const useMyProgress = () => {
  const { activeProduct, isLoading: productLoading } = useProductFeatures();
  const productCode = activeProduct?.code;

  const { data, error, isLoading, isFetching, refetch } =
    useQuery<ClimberProgress | null>({
      queryKey: ["myProgress", productCode],
      queryFn: async () => {
        const resp = await graphQLClient.request<GetMyProgressResponse>(
          GET_MY_PROGRESS,
          { productCode },
        );
        return resp.getMyProgress ?? null;
      },
      enabled: !!productCode,
    });

  useReactQueryErrorReporter("Could not load your progress", error, false);

  const ladderProgress: LadderProgress[] = data?.ladderProgress ?? [];
  const facets: ClimberFacets[] = data?.facets ?? [];
  const profileChoices: ProfileChoices | null = data?.profileChoices ?? null;

  return {
    progress: data ?? null,
    ladderProgress,
    facets,
    profileChoices,
    productCode,
    loading: isLoading || productLoading,
    isFetching,
    error,
    refetch,
  };
};
